/**
 * Environment Configuration with Zod Validation
 *
 * Provides type-safe access to environment variables.
 * Validates all required variables at application startup.
 */

import { z } from 'zod'

/**
 * Environment variable schema
 * All VITE_ prefixed variables are exposed to the client
 */
const envSchema = z.object({
  // Application
  VITE_APP_NAME: z.string().default('Admin Panel'),

  // API Configuration
  // Absolute URL (http://host/api/v1) VEYA root-relative path (/api/v1) kabul.
  // Prod'da nginx /api/ → API proxy ile same-origin kullanilir; o yuzden
  // relative path gecerlidir. Bos string veya gecersiz format reddedilir.
  VITE_API_URL: z
    .string()
    .refine(
      (v) => /^https?:\/\/.+/.test(v) || v.startsWith('/'),
      'VITE_API_URL must be an absolute http(s) URL or a root-relative path (/...)'
    )
    .default('http://localhost:3000/api/v1'),

  // Feature Flags
  VITE_ENABLE_DEBUG: z
    .string()
    .default('false')
    .transform((v) => v === 'true'),

  VITE_ENABLE_MOCK_API: z
    .string()
    .default('false')
    .transform((v) => v === 'true'),
})

/**
 * Validate environment variables
 * Fails fast if any required variable is missing or invalid
 */
function validateEnv() {
  const result = envSchema.safeParse(import.meta.env)

  if (!result.success) {
    console.error('Environment validation failed:')
    console.error(result.error.format())

    // In development, show detailed error
    if (import.meta.env.DEV) {
      throw new Error(
        `Invalid environment configuration:\n${JSON.stringify(result.error.format(), null, 2)}`
      )
    }

    throw new Error('Invalid environment configuration')
  }

  return result.data
}

/**
 * Validated environment variables
 * Use this instead of import.meta.env for type-safe access
 */
export const env = validateEnv()

/**
 * Environment type for TypeScript
 */
export type Env = z.infer<typeof envSchema>

/**
 * Helper flags
 */
export const isDev = import.meta.env.DEV
export const isProd = import.meta.env.PROD
export const isDebug = env.VITE_ENABLE_DEBUG
